// ─── megamenu.js ──────────────────────────────────────────────────────────────
// Datos estáticos (fallback) del header / mega menú.
// Cuando Strapi esté disponible, estas constantes actúan como placeholder
// mientras llegan los datos dinámicos.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Categorías que se muestran en la barra de navegación del header (desktop).
 * El orden importa: se renderiza de izquierda a derecha.
 */
export const DEFAULT_CATEGORIES = [
  'Ofertas',
  'Lanzamientos',
  'Dermocosmética',
  'Fragancias',
  'Maquillaje',
  'Cuidado personal',
  'Niños y bebés',
  'Limpieza de hogar',
  'Electro belleza',
];

/**
 * Píldoras destacadas que aparecen en la versión móvil del nav.
 * `active: true` marca la seleccionada por defecto.
 */
export const MOBILE_FEATURED = [
  { label: 'Perfumería', active: true },
  { label: 'Hogar', active: true },
];

/**
 * Columnas de subcategorías del mega menú (placeholder estático).
 * Cada entrada tiene:
 *  - title  : título de la columna
 *  - items  : lista de ítems / subcategorías
 *
 * El componente CategoryNav referencia índices específicos de este array
 * según la categoría activa (ver getMegaColumnsForCategory).
 */
export const MEGA_COLUMNS = [
  // [0]
  {
    title: 'Cocina',
    items: ['Detergentes', 'Lava vajillas', 'Limpieza de superficies'],
  },
  // [1]
  {
    title: 'Baño',
    items: ['Desinfectantes', 'Pastillas de inodoro'],
  },
  // [2]
  {
    title: 'Pisos y muebles',
    items: ['Lavandina', 'Desinfectantes', 'Aromatizantes', 'Lustramuebles', 'Ceras y autobrillos'],
  },
  // [3]
  {
    title: 'Insecticidas y repelentes',
    items: ['Aerosoles', 'Repelentes', 'Aparatos y cebos'],
  },
  // [4]
  {
    title: 'Ropa',
    items: ['Jabones líquidos', 'Suavizantes'],
  },
  // [5]
  {
    title: 'Calzado',
    items: ['Brillos limpiadores', 'Pomadas'],
  },
  // [6]
  {
    title: 'Desodorante de ambiente',
    items: ['Aromatizantes', 'Desinfectantes'],
  },
  // [7]
  {
    title: 'Papeles',
    items: ['Pañuelos', 'Papel higiénico', 'Rollos de cocina', 'Servilletas'],
  },
  // [8]
  {
    title: 'Accesorios de limpieza',
    items: ['Mopas', 'Escobas', 'Esponjas', 'Guantes', 'Palas y cabos', 'Trapos de pisos y paños multiuso'],
  },
];

/**
 * Columnas de descuentos para la categoría "Ofertas", separadas por sección.
 * Cada ítem es { label, href } apuntando al catálogo filtrado por sección y %
 * Formato URL: /tienda?seccion=<sección>&descuento=<valor>
 */
const DESCUENTOS = [10, 20, 30, 40, 50, 60];

export const OFERTAS_PERFUMERIA = {
  title: 'Perfumería',
  items: DESCUENTOS.map((n) => ({
    label: `Hasta ${n}%`,
    href:  `/tienda?seccion=Perfumer%C3%ADa&descuento=${n}`,
  })),
};

export const OFERTAS_HOGAR = {
  title: 'Hogar',
  items: DESCUENTOS.map((n) => ({
    label: `Hasta ${n}%`,
    href:  `/tienda?seccion=Hogar&descuento=${n}`,
  })),
};

/**
 * Rutas que corresponden al Mega Menu "General".
 */
export const SECTION_GENERAL_PATHS = new Set([
  '/contacto',
  '/sucursales',
  '/nuestra-historia',
  '/preguntas-frecuentes',
  '/cambios-devoluciones',
  '/terminos-condiciones',
  '/arrepentimiento',
  '/metodo-envio',
  '/gift-card',
]);

/**
 * Mapeo de query params o secciones detectadas a su constante de contexto
 */
export const SECTION_MAP = {
  perfumeria: 'PERFUMERIA',
  hogar: 'HOGAR',
  Perfumería: 'PERFUMERIA',
  Hogar: 'HOGAR',
};

export function categoryUrl(nombre, context, extra = '', seccionOverride = '') {
  let seccion = seccionOverride;
  if (!seccion) {
    seccion = context === 'HOGAR' ? 'Hogar' : context === 'PERFUMERIA' ? 'Perfumería' : (SECTION_MAP[context] ? (context === 'Hogar' ? 'Hogar' : 'Perfumería') : '');
  }
  return `/tienda?categoria=${encodeURIComponent(nombre)}${seccion ? `&seccion=${encodeURIComponent(seccion)}` : ''}${extra}`;
}

export function menuCategoryUrl(nombre, context, seccionOverride = '') {
  let seccion = seccionOverride;
  if (!seccion) {
    seccion = context === 'HOGAR' ? 'Hogar' : context === 'PERFUMERIA' ? 'Perfumería' : (SECTION_MAP[context] ? (context === 'Hogar' ? 'Hogar' : 'Perfumería') : '');
  }
  const suffix = seccion ? `&seccion=${encodeURIComponent(seccion)}` : '';
  if (nombre === 'Ofertas') return `/tienda?descuento=todas${suffix}`;
  if (nombre === 'Lanzamientos') return `/tienda?orden=createdAt%3Adesc${suffix}`;
  return categoryUrl(nombre, context, '', seccion);
}

export function discountUrl(percent, context) {
  return menuCategoryUrl('Ofertas', context).replace('descuento=todas', `descuento=${encodeURIComponent(percent)}`);
}

export async function fetchCategoryTree(signal) {
  const all = [];
  let page = 1;
  let pages = 1;
  do {
    const res = await fetch(`${STRAPI_URL}/api/categorias?populate[subcategorias][populate][tipos]=*&pagination[pageSize]=100&pagination[page]=${page}&publicationState=live`, { signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    all.push(...(json.data || []));
    pages = json.meta?.pagination?.pageCount || 1;
    page++;
  } while (page <= pages);
  return all;
}

/**
 * URL base de Strapi (se puede sobreescribir con la variable de entorno).
 */
export const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';
